import { gql } from '@apollo/client';

import { AggregateStats, SearchCounts, SearchKeywordSets } from 'shared/models/search';
import { Profile } from 'shared/profile-search/profile';
import { MessageContent } from 'shared/types/agent-message-content';
import { EmailAddress } from 'shared/types/email-compose';

export interface AgentConversation {
    id: string;
    title: string;
    lastMessageAt: number;
    createdAt: number;
}

export const GET_AGENT_CONVERSATIONS = gql`
    query GetAgentConversations($userId: String!) {
        agent_conversations: get_user_agent_conversations(
            args: { user_id: $userId }
            order_by: { lastMessageAt: desc }
        ) {
            id
            title
            lastMessageAt
            createdAt
        }
    }
`;

export interface AgentMessage {
    id: string;
    role: 'user' | 'assistant';
    content: MessageContent;
    createdAt: number;
    userId: string;
}

export const GET_AGENT_CONVERSATION_MESSAGES = gql`
    query GetAgentConversationMessages($conversationId: uuid!) {
        agent_conversations_by_pk(id: $conversationId) {
            id
            model
            messages(order_by: { createdAt: asc }) {
                id
                role
                content
                createdAt
                userId
            }
        }
    }
`;

// Email composer data queries for agent email drafts

export interface EmailAccount {
    email: string;
    name?: string;
}

export interface EmailMessageData {
    messageId: string;
    account: string;
    threadId: string;
    subject: string;
    internalDate: number;
    body: string;
    references: string | null;
    referenceMessageId: string | null;
    to: EmailAddress[];
    from: EmailAddress;
    cc: EmailAddress[];
    bcc: EmailAddress[];
}

// Query 1: Get recruiter info (accounts, signature) and person contacts
export interface AgentEmailComposerRecruiterData {
    candidate: {
        recruiter: {
            id: string;
            name: string;
            signature: string;
            emailAccounts: EmailAccount[];
        };
        person: {
            contacts: Array<{ value: string }>;
        };
    } | null;
}

export const AGENT_EMAIL_COMPOSER_RECRUITER_DATA = gql`
    query AgentEmailComposerRecruiterData($personId: String!, $jobId: String!) {
        candidate: candidates_by_pk(personId: $personId, jobId: $jobId) {
            recruiter {
                id
                name(path: "first")
                signature
                emailAccounts(where: { syncStatus: { _eq: "enabled" } }) {
                    email
                    name(path: "full")
                }
            }
            person {
                contacts(where: { channel: { _eq: "email" }, invalid: { _eq: false } }, limit: 1) {
                    value
                }
            }
        }
    }
`;

// Query 2: Get email history from specific recruiter accounts
export interface AgentEmailComposerEmailHistoryData {
    jobEmailMessages: EmailMessageData[];
    allEmailMessages: EmailMessageData[];
}

export const AGENT_EMAIL_COMPOSER_EMAIL_HISTORY = gql`
    query AgentEmailComposerEmailHistory($personId: String!, $jobId: String!, $recruiterEmails: [String!]!) {
        jobEmailMessages: communications(
            where: {
                personIds: { _has_key: $personId }
                jobIds: { _has_key: $jobId }
                account: { _in: $recruiterEmails }
                bounceReport: { _eq: false }
                category: { _eq: "candidate" }
            }
            order_by: { internalDate: desc }
            limit: 1
        ) {
            messageId
            account
            threadId
            subject
            internalDate
            body
            references
            referenceMessageId
            to: headers(path: "to")
            from: headers(path: "from")
            cc: headers(path: "cc")
            bcc: headers(path: "bcc")
        }
        allEmailMessages: communications(
            where: {
                personIds: { _has_key: $personId }
                account: { _in: $recruiterEmails }
                bounceReport: { _eq: false }
                category: { _eq: "candidate" }
            }
            order_by: { internalDate: desc }
            limit: 1
        ) {
            messageId
            account
            threadId
            subject
            internalDate
            body
            references
            referenceMessageId
            to: headers(path: "to")
            from: headers(path: "from")
            cc: headers(path: "cc")
            bcc: headers(path: "bcc")
        }
    }
`;

export interface AgentDraftEmail {
    id: string;
    conversationId: string;
    recipientType: 'candidate' | 'client' | 'internal';
    personId: string | null;
    jobId: string | null;
    clientId: string | null;
    subject: string;
    body: string;
    sentAt: number | null;
    createdAt: number;
    modifiedAt: number;
}

export const GET_AGENT_DRAFT_EMAIL = gql`
    query GetAgentDraftEmail($id: String!) {
        draft: agent_drafts_emails_by_pk(id: $id) {
            id
            conversationId
            recipientType
            personId
            jobId
            clientId
            subject
            body
            sentAt
            createdAt
            modifiedAt
        }
    }
`;

export const AGENT_EMAIL_COMPOSER_USER_DATA = gql`
    query AgentEmailComposerUserData($userEmail: String!) {
        emailAccounts: email_accounts(where: { email: { _eq: $userEmail }, syncStatus: { _eq: "enabled" } }, limit: 1) {
            email
            name(path: "full")
        }
    }
`;

// Submission draft types and queries

export interface AgentDraftSubmissionAttachment {
    filename: string;
    key: string;
    size: number;
}

export interface AgentDraftSubmission {
    id: string;
    conversationId: string;
    personId: string;
    jobId: string;
    sourceJobId: string | null;
    body: string;
    attachments: AgentDraftSubmissionAttachment[];
    usedAt: number | null;
    createdAt: number;
    modifiedAt: number;
    candidate: {
        _id: string;
        stage: {
            id: number;
            name: string;
        };
        assignee: string;
    } | null;
}

export const GET_AGENT_DRAFT_SUBMISSION = gql`
    query GetAgentDraftSubmission($id: String!) {
        draft: agent_drafts_submissions_by_pk(id: $id) {
            id
            conversationId
            personId
            jobId
            sourceJobId
            body
            attachments
            usedAt
            createdAt
            modifiedAt
            candidate {
                _id
                stage: job_stage {
                    id
                    name
                }
                assignee
            }
        }
    }
`;

export const MARK_AGENT_DRAFT_SUBMISSION_USED = gql`
    mutation MarkAgentDraftSubmissionUsed($id: String!, $usedAt: bigint!) {
        update_agent_drafts_submissions_by_pk(pk_columns: { id: $id }, _set: { usedAt: $usedAt }) {
            id
            usedAt
        }
    }
`;

export interface ExistingSubmissionNote {
    id: string;
    attachments: Array<{ filename: string; key: string; size: number }>;
    modifiedAt: number;
}

export const GET_EXISTING_SUBMISSION_NOTE = gql`
    query GetExistingSubmissionNote($personId: String!, $jobId: String!) {
        notes(
            where: {
                notable: { _eq: $personId }
                context: { _contains: { isSubmissionNote: true, jobId: $jobId } }
                _and: [
                    { _not: { context: { _has_key: "isSubmissionNoteChangeRequest" } } }
                    { _not: { context: { _has_key: "isSubmissionNoteReply" } } }
                    { _not: { context: { _has_key: "isATSSubmissionNote" } } }
                ]
                deleted: { _eq: false }
            }
            order_by: { modifiedAt: desc }
            limit: 1
        ) {
            id
            attachments
            modifiedAt
        }
    }
`;

// Client update draft types and queries

export interface ClientUpdateDiff {
    old: Record<string, unknown>;
    new: Record<string, unknown>;
}

export interface AgentDraftClientUpdate {
    id: string;
    conversationId: string;
    clientId: string;
    sourceDescription: string | null;
    updates: ClientUpdateDiff;
    explanation: string | null;
    appliedAt: number | null;
    appliedBy: string | null;
    dismissedAt: number | null;
    dismissedBy: string | null;
    createdAt: number;
    modifiedAt: number;
    client: {
        name: string;
        accountManagerId: string | null;
        description: string | null;
        salesPitch: string | null;
        funding: string | null;
        teamSize: string | null;
        hq: string | null;
        website: string | null;
        foundedDate: string | null;
    } | null;
}

export const GET_AGENT_DRAFT_CLIENT_UPDATE = gql`
    query GetAgentDraftClientUpdate($id: String!) {
        draft: agent_drafts_client_updates_by_pk(id: $id) {
            id
            conversationId
            clientId
            sourceDescription
            updates
            explanation
            appliedAt
            appliedBy
            dismissedAt
            dismissedBy
            createdAt
            modifiedAt
            client {
                name
                accountManagerId
                description
                salesPitch
                funding
                teamSize
                hq
                website
                foundedDate
            }
        }
    }
`;

export const APPLY_CLIENT_UPDATE_DRAFT = gql`
    mutation ApplyClientUpdateDraft($id: String!, $appliedAt: bigint!, $appliedBy: String!) {
        update_agent_drafts_client_updates_by_pk(
            pk_columns: { id: $id }
            _set: { appliedAt: $appliedAt, appliedBy: $appliedBy }
        ) {
            id
            appliedAt
            appliedBy
        }
    }
`;

export const DISMISS_CLIENT_UPDATE_DRAFT = gql`
    mutation DismissClientUpdateDraft($id: String!, $dismissedAt: bigint!, $dismissedBy: String!) {
        update_agent_drafts_client_updates_by_pk(
            pk_columns: { id: $id }
            _set: { dismissedAt: $dismissedAt, dismissedBy: $dismissedBy }
        ) {
            id
            dismissedAt
            dismissedBy
        }
    }
`;

export const UNDO_CLIENT_UPDATE_DRAFT_APPLY = gql`
    mutation UndoClientUpdateDraftApply($id: String!) {
        update_agent_drafts_client_updates_by_pk(pk_columns: { id: $id }, _set: { appliedAt: null, appliedBy: null }) {
            id
            appliedAt
            appliedBy
        }
    }
`;

export const UNDO_CLIENT_UPDATE_DRAFT_DISMISS = gql`
    mutation UndoClientUpdateDraftDismiss($id: String!) {
        update_agent_drafts_client_updates_by_pk(
            pk_columns: { id: $id }
            _set: { dismissedAt: null, dismissedBy: null }
        ) {
            id
            dismissedAt
            dismissedBy
        }
    }
`;

// Job update draft types and queries

export interface JobUpdateDiff {
    job?: { old: Record<string, unknown>; new: Record<string, unknown> };
    jobDescription?: { old: Record<string, unknown>; new: Record<string, unknown> };
}

export interface AgentDraftJobUpdate {
    id: string;
    conversationId: string;
    jobId: string;
    sourceDescription: string | null;
    updates: JobUpdateDiff;
    explanation: string | null;
    appliedAt: number | null;
    appliedBy: string | null;
    dismissedAt: number | null;
    dismissedBy: string | null;
    createdAt: number;
    modifiedAt: number;
    job: {
        title: string;
        clientId: string;
        accountManagerId: string | null;
        submissionSettings: Record<string, unknown> | null;
        introSettings: Record<string, unknown> | null;
        scoringAIContext: Record<string, unknown> | null;
        outreachAIContext: Record<string, unknown> | null;
        submissionGenAIContext: Record<string, unknown> | null;
        client: {
            name: string;
        } | null;
        description: {
            description: string | null;
            location: string | null;
            visa: string | null;
            compensation: string | null;
            minExperienceYears: number | null;
            maxExperienceYears: number | null;
            diversity: string | null;
            hiringManager: string | null;
            interviewPrep: string | null;
        } | null;
    } | null;
}

export const GET_AGENT_DRAFT_JOB_UPDATE = gql`
    query GetAgentDraftJobUpdate($id: String!) {
        draft: agent_drafts_job_updates_by_pk(id: $id) {
            id
            conversationId
            jobId
            sourceDescription
            updates
            explanation
            appliedAt
            appliedBy
            dismissedAt
            dismissedBy
            createdAt
            modifiedAt
            job {
                title
                clientId
                accountManagerId
                submissionSettings
                introSettings
                scoringAIContext
                outreachAIContext
                submissionGenAIContext
                client {
                    name
                }
                description {
                    description
                    location
                    visa
                    compensation
                    minExperienceYears
                    maxExperienceYears
                    diversity
                    hiringManager
                    interviewPrep
                }
            }
        }
    }
`;

export const APPLY_JOB_UPDATE_DRAFT = gql`
    mutation ApplyJobUpdateDraft($id: String!, $appliedAt: bigint!, $appliedBy: String!) {
        update_agent_drafts_job_updates_by_pk(
            pk_columns: { id: $id }
            _set: { appliedAt: $appliedAt, appliedBy: $appliedBy }
        ) {
            id
            appliedAt
            appliedBy
        }
    }
`;

export const DISMISS_JOB_UPDATE_DRAFT = gql`
    mutation DismissJobUpdateDraft($id: String!, $dismissedAt: bigint!, $dismissedBy: String!) {
        update_agent_drafts_job_updates_by_pk(
            pk_columns: { id: $id }
            _set: { dismissedAt: $dismissedAt, dismissedBy: $dismissedBy }
        ) {
            id
            dismissedAt
            dismissedBy
        }
    }
`;

export const UNDO_JOB_UPDATE_DRAFT_APPLY = gql`
    mutation UndoJobUpdateDraftApply($id: String!) {
        update_agent_drafts_job_updates_by_pk(pk_columns: { id: $id }, _set: { appliedAt: null, appliedBy: null }) {
            id
            appliedAt
            appliedBy
        }
    }
`;

export const UNDO_JOB_UPDATE_DRAFT_DISMISS = gql`
    mutation UndoJobUpdateDraftDismiss($id: String!) {
        update_agent_drafts_job_updates_by_pk(pk_columns: { id: $id }, _set: { dismissedAt: null, dismissedBy: null }) {
            id
            dismissedAt
            dismissedBy
        }
    }
`;

// Search preview types and queries

export interface SearchDraftMetadata {
    id: string;
    strategy: string;
    title: string;
    counts: SearchCounts | null;
    searchId: string;
    jobId: string;
    userId: string;
    aggregateStats: AggregateStats | null;
    keywordSets: SearchKeywordSets | null;
}

export interface SearchProfilesResult {
    profiles: Array<{
        personId: string;
        profile: Profile;
        viewedAt: number | null;
    }>;
    error?: string;
}

export const GET_SEARCH_DRAFT_METADATA = gql`
    query GetSearchDraftMetadata($id: String!) {
        agent_drafts_searches_by_pk(id: $id) {
            id
            strategy
            title
            counts
            searchId
            jobId
            userId
            aggregateStats
            keywordSets
        }
    }
`;

export const GET_AGENT_SEARCH_PROFILES = gql`
    query GetAgentSearchProfiles($searchDraftId: String!, $limit: Int!, $offset: Int!) {
        getAgentSearchProfiles(searchDraftId: $searchDraftId, limit: $limit, offset: $offset) {
            profiles {
                personId
                profile
                viewedAt
            }
            error
        }
    }
`;

export const REFRESH_AGENT_SEARCH_PROFILES = gql`
    mutation RefreshAgentSearchProfiles($searchDraftId: String!, $limit: Int!, $offset: Int!) {
        refreshAgentSearchProfiles(searchDraftId: $searchDraftId, limit: $limit, offset: $offset) {
            profiles {
                personId
                profile
                viewedAt
            }
            error
        }
    }
`;

export interface SaveAgentSearchResult {
    searchId?: string;
    error?: string;
}

export const SAVE_AGENT_SEARCH = gql`
    mutation SaveAgentSearch($searchDraftId: String!) {
        saveAgentSearch(searchDraftId: $searchDraftId) {
            searchId
            error
        }
    }
`;
